import { defineStore } from 'pinia'

import { useLocalization } from '@/shared/composables/localization'
import { ScenarioValue, SubscriberServices } from '@/shared/types/settings'
import { getCreatedEntityGuidFrom201Response } from '@/shared/utils/http'

import { ForwardingConditions, Forwarding } from '..'
import { useSettingsApi } from '../api'
import { isSameForwarding } from '../lib/is-same-forwarding'

export const useStoreSettingsForwarding = defineStore('forwarding', {
  state: () => ({
    forwarding: new Map<ForwardingConditions, Forwarding[]>([[ForwardingConditions.ALL, []]]),
    isForwardingsLoading: false,
    isForwardingSaving: false,
    subscriberServices: new Map<string, SubscriberServices>(),
    scenarios: new Map<string, ScenarioValue>(),
    isForwardingEnabledBeforeLoading: false,
  }),

  getters: {
    getForwardingByCondition (state) {
      return (condition: ForwardingConditions) => state.forwarding.get(condition)
    },

    getForwardingByGuid (state) {
      return (condition: ForwardingConditions, guid: string) => state.forwarding.get(condition)?.find(item => item.guid === guid)
    },

    getSubscriberServicesFromArray (state) {
      return Array.from(state.subscriberServices, ([, value]) => value)
    },

    dndForward (state) {
      return state.forwarding.get(ForwardingConditions.ALL)?.filter((item) => item.service.value === 'DoNotDisturb')?.[0]
    },
    isDndEnabled (state) {
      return !!(state.forwarding.get(ForwardingConditions.ALL)?.filter((item) => item.service.value === 'DoNotDisturb' && item.enabled)?.length ?? 0 > 0)
    },
    dndServiceKey (state) {
      let result = ''
      state.subscriberServices.forEach(([key, item]) => {
        if (item === 'DoNotDisturb') {
          result = key!
        }
      })
      return result
    },
    maxPriority ({ forwarding }) {
      return forwarding.get(ForwardingConditions.ALL)?.reduce((max, item) => {
        return Math.max(max, item.priority)
      }, 0) ?? 0
    },
    maxPriorityCallForwardingOnBusy (state) {
      const rules = state.forwarding.get(ForwardingConditions.BUSY)
      if (!rules) return null
      const result = rules.reduce((acc, rule) => {
        if (rule.enabled && rule.priority > acc.priority) {
          return rule
        } else {
          return acc
        }
      }, { priority: -Infinity } as Forwarding)
      if (result.priority === -Infinity) return null
      return result
    },
    isForwardingEnabled (state) {
      return !!state.forwarding.get(ForwardingConditions.ALL)?.find(f => f.service.value === SubscriberServices.Forward && f.enabled && f.condition === ForwardingConditions.UNCONDITIONAL) ||
        (state.isForwardingsLoading && state.isForwardingEnabledBeforeLoading)
    },
  },

  actions: {
    setForwarding (condition: ForwardingConditions, forwarding: Forwarding) {
      const forwardings = this.forwarding.get(condition)

      if (!forwardings) return

      const index = forwardings.findIndex(item => isSameForwarding(item, forwarding))

      if (index === -1) return

      forwardings[index] = forwarding
    },

    removeForwardingFromStore (forwarding: Forwarding) {
      const forwardings = this.forwarding.get(forwarding.condition as ForwardingConditions)

      if (!forwardings) return

      this.forwarding.set(
        forwarding.condition as ForwardingConditions,
        forwardings.filter((item: Forwarding) => !isSameForwarding(item, forwarding)),
      )

      const allForwardings = this.forwarding.get(ForwardingConditions.ALL)

      if (!allForwardings) return

      this.forwarding.set(
        ForwardingConditions.ALL,
        allForwardings.filter((item: Forwarding) => !isSameForwarding(item, forwarding)),
      )
    },

    addForwardingToStore (forwarding: Forwarding) {
      const forwardings = this.forwarding.get(forwarding.condition as ForwardingConditions)

      if (!forwardings) return

      this.forwarding.set(
        forwarding.condition as ForwardingConditions,
        [...forwardings, ...[forwarding]],
      )

      const allForwardings = this.forwarding.get(ForwardingConditions.ALL)

      if (!allForwardings) return

      this.forwarding.set(
        ForwardingConditions.ALL,
        [...allForwardings, ...[forwarding]],
      )
    },

    async loadAllForwardings () {
      const api = useSettingsApi()

      if (this.isForwardingsLoading) {
        return
      }

      try {
        this.isForwardingEnabledBeforeLoading = this.isForwardingEnabled

        this.isForwardingsLoading = true

        this.forwarding = new Map()

        const { data = [] } = await api.getForwarding()

        data.forEach((rule: Forwarding) => {
          const condition = rule.condition

          if (this.forwarding.has(condition)) {
            this.forwarding.set(condition, [...this.forwarding.get(condition) as Forwarding[], ...[rule]])
          } else {
            this.forwarding.set(condition, [rule])
          }
        })

        this.forwarding.set(ForwardingConditions.ALL, data)
      } catch (e) {
        console.error(e)
      } finally {
        this.isForwardingsLoading = false
      }
    },

    async loadSubscriberServices () {
      const api = useSettingsApi()
      const { data } = await api.getSubscriberServices()

      for (const key in data) {
        this.subscriberServices.set(key, data[key]!)
      }
    },

    async loadScenarios () {
      const api = useSettingsApi()
      const { data } = await api.getScenarios()

      for (const key in data) {
        this.scenarios.set(key, data[key]!)
      }
    },

    async removeForwardingItem (guid: string) {
      const api = useSettingsApi()
      try {
        await api.removeForwardingItem(guid)
      } catch (e) {
        console.error(e)
        const { t } = useLocalization()
        throw new Error(t('common.failedToRemoveForwarding'))
      }
    },

    async addForwarding (forwarding: Forwarding) {
      this.isForwardingSaving = true

      const api = useSettingsApi()

      try {
        const result = await api.addForwarding(forwarding)

        const guid = getCreatedEntityGuidFrom201Response(result)
        if (guid) {
          forwarding.guid = guid
        } else {
          await this.loadAllForwardings()
        }
      } catch (e) {
        console.error(e)
        const { t } = useLocalization()
        throw new Error(t('common.failedToCreateForwarding'))
      } finally {
        this.isForwardingSaving = false
      }
    },

    async saveForwarding (forwarding: Forwarding) {
      this.isForwardingSaving = true

      const api = useSettingsApi()
      try {
        await api.saveForwarding(forwarding)
      } catch (e) {
        console.error(e)
        const { t } = useLocalization()
        throw new Error(t('common.errorSavingData'))
      } finally {
        this.isForwardingSaving = false
      }
    },
  },
})
